import React, { createContext, useCallback, useContext, useState } from "react";

import {
  getNotasByStudent,
  getNotasByAsignatura,
  getNotasByDirector,
  getPeriodos,
  createNota,
  updateNota,
  deleteNota,
  getNotasForAdmin,
  obtenerPlanillaNotas,
  guardarNotasMasivas,
  obtenerMotivos,
  agregarMotivo,
  eliminarMotivo,
  editarMotivo,
  obtenerPlanillaConMotivos,
  guardarCalificacionesMotivos,

  verificarAccesoEstudianteNotas, 
  verificarAccesoPeriodoNotas, 
  getInhabilitadosNotas, 
  gestionarVisibilidadNotas,

  obtenerAsignaturasSalon,
  verificarAsignaturaSalon
} from "../services/notasService";
import { useAuth } from "./AuthContext";

const NotasContext = createContext();

export const useNotas = () => useContext(NotasContext);

export const NotasProvider = ({ children }) => {
  const { user } = useAuth();
  const [notas, setNotas] = useState([]);
  const [notasLoaded, setNotasLoaded] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [periodosLoaded, setPeriodosLoaded] = useState(false);
  
  // ✅ NUEVO: Estado para asignaturas del salón
  const [asignaturasSalon, setAsignaturasSalon] = useState([]);
  const [loadingAsignaturas, setLoadingAsignaturas] = useState(false);

  const [loading, setLoading] = useState(false);

  // 🆕 Estados para carga masiva
  const [planillaNotas, setPlanillaNotas] = useState([]);
  const [loadingPlanilla, setLoadingPlanilla] = useState(false);

  const [motivosNotas, setMotivosNotas] = useState([]);
  const [planillaConMotivos, setPlanillaConMotivos] = useState({ estudiantes: [], motivos: [], es_preescolar: false });

  const [inhabilitadosNotas, setInhabilitadosNotas] = useState([]);
  const [loadingInhabilitadosNotas, setLoadingInhabilitadosNotas] = useState(false);

  // NUEVO: Verificar acceso de un estudiante a notas
  const verificarAccesoNotas = useCallback(async (studentId) => {
    try {
      const response = await verificarAccesoEstudianteNotas(studentId);
      return response.tieneAcceso;
    } catch (error) {
      return false;
    }
  }, []);

  // NUEVO: Verificar acceso a un periodo
  const verificarAccesoAPeriodoNotas = useCallback(async (studentId, periodoId) => {
    try {
      const response = await verificarAccesoPeriodoNotas(studentId, periodoId);
      return response.tieneAcceso;
    } catch (error) {
      return false;
    }
  }, []);

  // NUEVO: Obtener lista de inhabilitados
  const fetchInhabilitadosNotas = useCallback(async () => {
    setLoadingInhabilitadosNotas(true);
    try {
      const response = await getInhabilitadosNotas();
      if (response.success) {
        setInhabilitadosNotas(response.data || []);
        return response.data;
      }
      return [];
    } catch (error) {
      
      return [];
    } finally {
      setLoadingInhabilitadosNotas(false);
    }
  }, []);

  // NUEVO: Gestionar visibilidad (habilitar/inhabilitar)
  const gestionarVisibilidadNotasData = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await gestionarVisibilidadNotas(data);
      
      if (response.success) {
        await fetchInhabilitadosNotas();
      }
      
      return response;
    } catch (error) {
      
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchInhabilitadosNotas]);

  // NUEVO: Habilitar estudiante específico
  const habilitarEstudianteNotas = useCallback(async (studentId) => {
    try {
      const response = await gestionarVisibilidadNotas({
        filterType: "estudiante",
        actionType: "habilitar",
        selectedEstudiante: studentId,
        motivo: "Habilitación manual desde lista de inhabilitados",
      });

      if (response.success) {
        await fetchInhabilitadosNotas();
      }

      return response;
    } catch (error) {
      
      throw error;
    }
  }, [fetchInhabilitadosNotas]);

  const fetchNotasByStudent = useCallback(async (force = false) => {
    if (!notasLoaded || force) {
      setLoading(true);
      try {
        const data = await getNotasByStudent();
        setNotas(data || []);
        setNotasLoaded(true);
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    }
  }, [notasLoaded]);

  const fetchNotasByAsignatura = async (asignaturaId) => {
    setLoading(true);
    try {
      const data = await getNotasByAsignatura(asignaturaId);
      setNotas(data || []);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const fetchNotasByDirector = useCallback(async (force = false) => {
    if (!notasLoaded || force) {
      setLoading(true);
      try {
        const data = await getNotasByDirector();
        setNotas(data || []);
        setNotasLoaded(true);
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    }
  }, [notasLoaded]);

  const fetchNotasForAdmin = async (force = false) => {
    setLoading(true);
    try {
      const data = await getNotasForAdmin();
      setNotas(data || []);
      setNotasLoaded(true);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodos = useCallback(async (force = false) => {
    if (!periodosLoaded || force) {
      setLoading(true);
      try {
        const data = await getPeriodos();
        setPeriodos(data || []);
        setPeriodosLoaded(true);
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    }
  }, [periodosLoaded]);
  
  // useEffect(() => {
  //   if (!notas.length && location.pathname === "/notas") {
  //     fetchNotasByDirector(); // Llama a la función para cargar los pagos
  //   }
  // }, [notas, fetchNotasByDirector, location.pathname]);

  const createNotaData = async (notaData) => {
    const response = await createNota(notaData);
    // Recargar notas según el rol
    if (user?.rol_id === 1) {
      await fetchNotasForAdmin(true);
    } else {
      await fetchNotasByDirector(true);
    }
    return response;
  };

  const updateNotaData = async (notaId, notaData) => {
    const response = await updateNota(notaId, notaData);
    // Recargar notas según el rol
    if (user?.rol_id === 1) {
      await fetchNotasForAdmin(true);
    } else {
      await fetchNotasByDirector(true);
    }
    return response;
  };

  const deleteNotaData = async (id) => {
    try{
      await deleteNota(id);
      // Elimina la nota del estado actual antes de volver a cargar los datos
      setNotas((prevNotas) => prevNotas.filter((nota) => nota.id !== id));
      // Vuelve a cargar las notas para asegurar la sincronización
      if (user?.rol_id === 1) {
        await fetchNotasForAdmin(true);
      } else {
        await fetchNotasByDirector(true);
      }
    } catch (error) {
      
    }
  };

  // 🆕 FUNCIONES PARA CARGA MASIVA
  const cargarPlanillaNotas = async (salonId, periodoId, asignaturaSalonId) => {
    setLoadingPlanilla(true);
    try {
      const response = await obtenerPlanillaNotas(salonId, periodoId, asignaturaSalonId);
      
      // 🔧 SIMPLIFICADO: Ahora el backend devuelve directamente el array
      setPlanillaNotas(response.data || []);
      
      return response;
    } catch (error) {
      
      throw error;
    } finally {
      setLoadingPlanilla(false);
    }
  };

  const guardarNotasMasivasData = async (data) => {
    try {
      const response = await guardarNotasMasivas(data);
      // Recargar planilla después de guardar
      if (data.salon_id && data.periodo_id && data.notas[0]?.asignatura_salon_id) {
        await cargarPlanillaNotas(
          data.salon_id, 
          data.periodo_id, 
          data.notas[0].asignatura_salon_id
        );
      }
      return response;
    } catch (error) {
      
      throw error;
    }
  };

  const limpiarPlanilla = useCallback(() => {
    setPlanillaNotas([]);
  }, []);

  const cargarMotivos = async (salonId, periodoId, asignaturaSalonId) => {
    try {
      const response = await obtenerMotivos(salonId, periodoId, asignaturaSalonId);
      setMotivosNotas(response.data || []);
      return response;
    } catch (error) {
      
      throw error;
    }
  };

  const agregarMotivoData = async (data) => {
    try {
      const response = await agregarMotivo(data);
      await cargarMotivos(data.salon_id, data.periodo_id, data.asignatura_salon_id);
      return response;
    } catch (error) {
      
      throw error;
    }
  };

  const editarMotivoData = async (motivoId, nuevoNombre) => {
    try {
      const response = await editarMotivo(motivoId, nuevoNombre);
      return response;
    } catch (error) {
      console.error("Error editando motivo:", error);
      throw error;
    }
  };

  const eliminarMotivoData = async (motivoId, salonId, periodoId, asignaturaSalonId) => {
    try {
      const response = await eliminarMotivo(motivoId);
      await cargarMotivos(salonId, periodoId, asignaturaSalonId);
      return response;
    } catch (error) {
      
      throw error;
    }
  };

  const cargarPlanillaConMotivos = useCallback(async (salonId, periodoId, asignaturaSalonId) => {
    setLoadingPlanilla(true);
    try {
      const response = await obtenerPlanillaConMotivos(salonId, periodoId, asignaturaSalonId);
      setPlanillaConMotivos(response.data);
      setMotivosNotas(response.data.motivos || []);
      return response;
    } catch (error) {
      console.error("Error cargando planilla con motivos:", error);
      throw error;
    } finally {
      setLoadingPlanilla(false);
    }
  }, []);

  const guardarCalificacionesMotivoData = async (calificaciones) => {
    try {
      const response = await guardarCalificacionesMotivos(calificaciones);
      return response;
    } catch (error) {
      
      throw error;
    }
  };

  // ✅ NUEVA: Función para obtener asignaturas del salón
  const getAsignaturasSalon = useCallback(async (salonId) => {
    if (!salonId) return;
    try {
      setLoadingAsignaturas(true);
      const response = await obtenerAsignaturasSalon(salonId);
      setAsignaturasSalon(response.data || []);
      return response;
    } catch (error) {
      console.error("Error obteniendo asignaturas del salón:", error);
      setAsignaturasSalon([]);
      throw error;
    } finally {
      setLoadingAsignaturas(false);
    }
  }, []);

  // ✅ NUEVA: Verificar si una asignatura está en un salón
  const verificarAsignaturaSalonData = async (salonId, asignaturaId) => {
    try {
      const response = await verificarAsignaturaSalon(salonId, asignaturaId);
      return response;
    } catch (error) {
      console.error("Error verificando asignatura_salon:", error);
      throw error;
    }
  };

  return (
    <NotasContext.Provider
      value={{
        notas,
        periodos,
        planillaNotas,
        loading,
        loadingPlanilla,
        fetchNotasByStudent,
        fetchNotasByAsignatura,
        fetchNotasByDirector,
        fetchNotasForAdmin,
        fetchPeriodos,
        createNotaData,
        updateNotaData,
        deleteNotaData,


        cargarPlanillaNotas,
        guardarNotasMasivasData,
        limpiarPlanilla,
        motivosNotas,
        planillaConMotivos,
        cargarMotivos,
        agregarMotivoData,
        editarMotivoData,
        eliminarMotivoData,
        cargarPlanillaConMotivos,
        guardarCalificacionesMotivoData,

        // Gestión de visibilidad de notas
        inhabilitadosNotas,
        loadingInhabilitadosNotas,
        verificarAccesoNotas,
        verificarAccesoAPeriodoNotas,
        fetchInhabilitadosNotas,
        gestionarVisibilidadNotasData,
        habilitarEstudianteNotas,

        asignaturasSalon,
        loadingAsignaturas,
        getAsignaturasSalon,
        verificarAsignaturaSalonData,
      }}
    >
      {children}
    </NotasContext.Provider>
  );
};
